import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiPackage } = FiIcons;

const CATEGORIES = ['Equips', 'Cards', 'Talisman', 'Accessories', 'Costume'];
const CONDITIONS_1 = ['Any', 'Class Usable', 'For Selling'];

const DropsConfig = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    condition1: '',
    condition2: '',
    is_limit: false,
    rate: ''
  });

  useEffect(() => {
    if (user) fetchItems();
  }, [user]);

  const fetchItems = async () => {
    const { data } = await supabase
      .from(TABLES.ITEMS)
      .select('*')
      .eq('master_id', user.id)
      .order('created_at', { ascending: false });
    setItems(data || []);
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        category: item.category,
        condition1: item.condition1,
        condition2: item.condition2 || '',
        is_limit: item.is_limit,
        rate: item.rate
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        category: '',
        condition1: '',
        condition2: '',
        is_limit: false,
        rate: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category || !formData.condition1 || formData.rate === '') {
      alert('Please fill in all mandatory fields.');
      return;
    }
    setLoading(true);
    const payload = {
      ...formData,
      rate: parseFloat(formData.rate),
      master_id: user.id
    };

    let error;
    if (editingItem) {
      const { error: err } = await supabase.from(TABLES.ITEMS).update(payload).eq('id', editingItem.id);
      error = err;
    } else {
      const { error: err } = await supabase.from(TABLES.ITEMS).insert([payload]);
      error = err;
    }

    setLoading(false);
    if (!error) {
      alert(editingItem ? 'Item updated successfully!' : 'New item added to catalog!');
      setIsModalOpen(false);
      fetchItems();
    } else {
      alert('Error saving item: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (confirm('Delete this item? This will affect future drop logs.')) {
      const { error } = await supabase.from(TABLES.ITEMS).delete().eq('id', id);
      if (!error) {
        alert('Item removed from catalog.');
        fetchItems();
      } else {
        alert(error.message);
      }
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">Drops Catalog</h2>
          <p className="text-gray-400 font-bold mt-1">Configure pricing and conditions.</p>
        </div>
        <button onClick={() => openModal()} className="apple-button-primary flex items-center justify-center gap-2 shadow-xl w-full sm:w-auto">
          <FiPlus className="text-xl" /> New Item
        </button>
      </header>

      <div className="bento-card !p-0 overflow-hidden">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-left min-w-[800px]">
            <thead className="bg-[#f5f5f7] border-b border-gray-100">
              <tr>
                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Item Identity</th>
                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</th>
                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Rate (Php)</th>
                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-gray-50/50 transition-colors group">
                  <td className="px-6 py-5">
                    <div className="font-black text-gray-900 text-base">{item.name}</div>
                  </td>
                  <td className="px-6 py-5">
                    <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-tighter">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-6 py-5 font-black text-right text-lg text-emerald-600">
                    ₱{item.rate.toLocaleString()}
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="flex justify-center gap-1">
                      <button onClick={() => openModal(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-all"><FiEdit2 /></button>
                      <button onClick={() => handleDelete(item.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-all"><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length === 0 && (
          <div className="p-16 text-center text-gray-400 font-bold">Catalog is empty.</div>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsModalOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.9, opacity: 0, y: 30 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 30 }} className="relative w-full max-w-2xl bg-white rounded-[2rem] md:rounded-[3rem] p-6 md:p-12 shadow-2xl overflow-y-auto max-h-[90vh]" >
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center shadow-lg"><FiPackage /></div>
                  <h3 className="text-xl md:text-2xl font-black text-gray-900">{editingItem ? 'Edit Item' : 'New Item'}</h3>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="p-2 bg-gray-100 rounded-full text-gray-400 hover:bg-gray-200 transition-all"><FiX /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Item Name *</label>
                  <input className="apple-input" placeholder="e.g., GTB Card" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Category *</label>
                  <select className="apple-input" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} >
                    <option value="">Select</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Condition 1 *</label>
                  <select className="apple-input" value={formData.condition1} onChange={e => setFormData({ ...formData, condition1: e.target.value })} >
                    <option value="">Select</option>
                    {CONDITIONS_1.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Rate (Php) *</label>
                  <div className="relative">
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 font-black text-gray-400 text-lg">₱</span>
                    <input type="number" className="apple-input !pl-12 text-xl font-black text-emerald-600" placeholder="0.00" value={formData.rate} onChange={e => setFormData({ ...formData, rate: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="mt-10 flex flex-col md:flex-row gap-4">
                <button onClick={() => setIsModalOpen(false)} className="apple-button-secondary w-full">Cancel</button>
                <button onClick={handleSave} disabled={loading} className="apple-button-primary w-full flex items-center justify-center gap-2">
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><FiSave /> {editingItem ? 'Update' : 'Create'}</>}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DropsConfig;